/**
 * SEO-byggare för de lokala landningssidorna (/husbil-kalix, /husvagn-kalix).
 *
 * Samma idé som listing-seo.ts: titel, beskrivning och strukturerad data byggs
 * från verkligt lager (antal, prisspann, märken) i stället för statisk text —
 * det ger både högre CTR i SERP och starkare lokal relevans för sökningar som
 * "husbilar kalix" och "husvagnar kalix". Canonical, robots och URL rörs inte.
 */

import { clampDescription } from "@/lib/seo-quality";
import { listingStats, SITE, type ListingLike } from "@/lib/listing-seo";
import { vehicleSlug } from "@/lib/vehicle-slug";

const nf = (n: number) => n.toLocaleString("sv-SE");

export interface LocalStockHeadInput {
  category: "husbil" | "husvagn";
  items: ListingLike[];
  /** Självrefererande kanonisk URL. */
  url: string;
  /** Ort som sidan handlar om, t.ex. "Kalix". */
  city: string;
  faq: { q: string; a: string }[];
  breadcrumbName: string;
  fallbackBrands: string[];
}

export function buildLocalStockHead({
  category,
  items,
  url,
  city,
  faq,
  breadcrumbName,
  fallbackBrands,
}: LocalStockHeadInput) {
  const plural = category === "husbil" ? "husbilar" : "husvagnar";
  const s = listingStats(items, category);
  const scoped = items.filter((i) => (i.category ?? "").toLowerCase().includes(category));

  const countPrefix = s.count > 0 ? `${s.count} ` : "";
  const priceBit = s.minPrice ? ` från ${nf(s.minPrice)} kr` : "";
  const title = `${countPrefix}${plural} i ${city}${priceBit} | AK Husbil & Fritid`;

  const brands = (s.brands.length >= 2 ? s.brands : fallbackBrands).slice(0, 3).join(", ");
  const description = clampDescription(
    `${countPrefix}kontrollerade ${plural} i lager i ${city} — ${brands} m.fl.` +
      `${s.minPrice ? ` Priser från ${nf(s.minPrice)} kr.` : ""}` +
      ` Fukttestade, inbyte och finansiering.`,
  );

  const itemList = {
    "@type": "ItemList",
    name: `${plural.charAt(0).toUpperCase()}${plural.slice(1)} i lager i ${city}`,
    numberOfItems: s.count,
    itemListElement: scoped.slice(0, 20).map((v, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `${SITE}/fordon/${vehicleSlug(v as never)}`,
      item: {
        "@type": category === "husbil" ? "Motorhome" : "Vehicle",
        name: v.title,
        url: `${SITE}/fordon/${vehicleSlug(v as never)}`,
        image: v.image ?? v.images?.[0],
        brand: v.brand ? { "@type": "Brand", name: v.brand } : undefined,
        vehicleModelDate: v.year ?? undefined,
        offers: v.price
          ? {
              "@type": "Offer",
              price: v.price,
              priceCurrency: "SEK",
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/UsedCondition",
              seller: { "@id": `${SITE}/#dealer` },
            }
          : undefined,
      },
    })),
  };

  return {
    meta: [
      { title },
      { name: "description", content: description },
      {
        name: "keywords",
        content: [
          `${plural} ${city}`,
          `${plural.slice(0, -1)} ${city}`,
          `köpa ${plural.slice(0, -1)} ${city}`,
          `begagnad ${plural.slice(0, -1)} ${city}`,
          `${category === "husbil" ? "husbilshandlare" : "husvagnshandlare"} ${city}`,
          `${plural} Norrbotten`,
          ...(s.brands.length ? s.brands : fallbackBrands).slice(0, 5),
        ].join(", "),
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: breadcrumbName, item: url },
              ],
            },
            {
              "@type": "CollectionPage",
              "@id": `${url}#collection`,
              url,
              name: title,
              description,
              inLanguage: "sv-SE",
              about: [
                { "@type": "Thing", name: `${plural} till salu` },
                { "@type": "Place", name: `${city}, Norrbotten` },
              ],
              mainEntity: itemList,
              provider: { "@id": `${SITE}/#dealer` },
            },
            {
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "AutoDealer",
              "@id": `${SITE}/#dealer`,
              name: "AK Husbil & Fritid",
              url: `${SITE}/`,
              telephone: "+46708629743",
              priceRange:
                s.minPrice && s.maxPrice ? `${nf(s.minPrice)}–${nf(s.maxPrice)} kr` : "$$",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Stabsvägen 5",
                postalCode: "952 51",
                addressLocality: "Kalix",
                addressRegion: "Norrbotten",
                addressCountry: "SE",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Norrbotten" },
                { "@type": "Country", name: "Sverige" },
              ],
            },
          ],
        }),
      },
    ],
  };
}
